import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronUp, CircleAlert, Plus, Route, Trash2 } from "lucide-react";
import { Collapsible, DropdownMenu, Select } from "radix-ui";
import {
  comboKey,
  FEATURE_IDS,
  FEATURE_LABELS,
  type EndpointCombo,
  type FeatureId,
} from "../../services/featureRoutes";
import { useSettingsStore } from "../../stores/settingsStore";
import { EVOLUTION_ROUTE_IDS, isUsableCombo, routeIsUsable } from "../setup/readiness";

interface FeatureGroup {
  id: string;
  title: string;
  description: string;
  defaultCollapsed?: boolean;
  features: FeatureId[];
}

const GROUPS: FeatureGroup[] = [
  { id: "core", title: "核心", description: "开始狩猎前必须接通的主叙事路由。", features: ["core-chat"] },
  { id: "evolution", title: "并发演化", description: "默认所有阶段共用统一路由；需要精细分流时再分别配置。", features: [...EVOLUTION_ROUTE_IDS, "evolution-bio"] },
  { id: "common", title: "常用玩法", description: "现场交谈、分支选项与自定义锻造等常用辅助功能。", features: ["branch-options", "quick-chat", "quick-chat-group", "crafting"] },
  { id: "story", title: "剧情与记忆", description: "剧情导演复用剧情演化路由；这里同时配置总结与叙事记忆。", defaultCollapsed: true, features: ["plot-evolution", "deep-summary", "narrative-memory"] },
  { id: "knowledge", title: "知识与检索", description: "查询改写与可选的语义向量接口。", defaultCollapsed: true, features: ["rag-query", "rag-embedding"] },
];

const RECOMMENDED_MODELS: Partial<Record<FeatureId, string>> = {
  "core-chat": "Gemini 3.1 Pro",
  evolution: "Gemini 3 Flash",
  "evolution-entry": "Gemini 3 Flash",
  "evolution-item": "Gemini 3 Flash",
  "evolution-player": "Gemini 3 Flash",
  "evolution-npc": "Gemini 3 Flash",
  "evolution-monster": "Gemini 3 Flash",
  "evolution-misc": "Gemini 3 Flash",
  "evolution-bio": "Gemini 3 Flash",
  "deep-summary": "Gemini 3 Flash",
  "quick-chat": "Gemini 3.1 Flash Lite",
  "quick-chat-group": "Gemini 3.1 Flash Lite",
  "rag-query": "Gemini 3.1 Flash Lite",
  "narrative-memory": "Gemini 3 Flash",
  "plot-evolution": "DeepSeek V4 Flash",
  crafting: "Gemini 3 Flash",
};

function comboIssue(combo: EndpointCombo | undefined, expectedKind: EndpointCombo["kind"]) {
  if (!combo) return "接口不存在";
  if (combo.kind !== expectedKind) return "类型不匹配";
  if (!combo.enabled) return "已禁用";
  if (!combo.baseUrl.trim()) return "缺少 API URL";
  if (!combo.model.trim()) return "缺少模型";
  if (!comboKey(combo.id).trim()) return "当前会话未填密钥";
  if (!isUsableCombo(combo, expectedKind)) return "API URL 不可用";
  return null;
}

function RouteSelector({ feature, required }: { feature: FeatureId; required: boolean }) {
  const settings = useSettingsStore();
  const expectedKind: EndpointCombo["kind"] = feature === "rag-embedding" ? "embedding" : "generation";
  const routeIds = settings.featureRoutes[feature] ?? [];
  const routes = routeIds.map((id) => ({ id, combo: settings.endpointCombos.find((item) => item.id === id) }));
  const available = settings.endpointCombos.filter((combo) => combo.kind === expectedKind && !routeIds.includes(combo.id));
  const usable = routes.flatMap(({ combo }) => combo && isUsableCombo(combo, expectedKind) ? [combo] : []);
  const primary = usable[0];
  const summary = primary ? `主：${primary.name} · 备用 ${Math.max(0, usable.length - 1)}` : routeIds.length ? "已分配，但没有可用接口" : "未分配";
  const setRoutes = (ids: string[]) => settings.setFeatureRoute(feature, ids);
  const move = (index: number, offset: number) => {
    const target = index + offset;
    if (target < 0 || target >= routeIds.length) return;
    const next = [...routeIds];
    [next[index], next[target]] = [next[target]!, next[index]!];
    setRoutes(next);
  };

  return <div className="mortal-route-selector">
    <div className="route-selector-head">
      <div><span>{FEATURE_LABELS[feature]}路由</span>{RECOMMENDED_MODELS[feature] && <small>推荐模型：{RECOMMENDED_MODELS[feature]}</small>}</div>
      {available.length > 0 ? <DropdownMenu.Root>
        <DropdownMenu.Trigger className="route-add-trigger"><Plus size={15}/>添加<ChevronDown size={14}/></DropdownMenu.Trigger>
        <DropdownMenu.Portal><DropdownMenu.Content className="route-dropdown" align="end" sideOffset={6}>
          {available.map((combo) => <DropdownMenu.Item className="route-dropdown-item" key={combo.id} onSelect={() => setRoutes([...routeIds, combo.id])}>
            <span><strong>{combo.name}</strong><small>{combo.model || "未选择模型"}</small></span>
            {comboIssue(combo, expectedKind) && <em>{comboIssue(combo, expectedKind)}</em>}
          </DropdownMenu.Item>)}
        </DropdownMenu.Content></DropdownMenu.Portal>
      </DropdownMenu.Root> : <small className="route-add-empty">{settings.endpointCombos.some((combo) => combo.kind === expectedKind) ? "所有接口均已添加" : `请先添加${expectedKind === "embedding" ? " Embedding" : "生成"}接口`}</small>}
    </div>
    {required && !primary && <div className="route-required-warning" role="alert"><CircleAlert size={17}/><span><strong>模型接口未接通</strong><small>当前玩法已开启，请添加并启用可用接口。</small></span></div>}
    {!routes.length ? <div className="route-empty">尚未接通接口，请从“添加”下拉菜单选择</div> : <div className="ordered-routes">
      {routes.map(({ id, combo }, index) => { const issue = comboIssue(combo, expectedKind); return <div className={issue ? "ordered-route has-issue" : "ordered-route"} key={id}>
        <span className={index === 0 ? "route-priority primary" : "route-priority"}>{index === 0 ? "主要" : `备用 ${index}`}</span>
        <div className="ordered-route-copy"><strong>{combo?.name ?? id}</strong><small>{combo?.model || "接口配置已丢失"}</small>{issue && <em>{issue}{issue === "已禁用" && " · 发送时自动跳过"}</em>}</div>
        <div className="ordered-route-actions">
          <button disabled={index === 0} onClick={() => move(index, -1)} aria-label={`上移${combo?.name ?? id}`}><ChevronUp/></button>
          <button disabled={index === routes.length - 1} onClick={() => move(index, 1)} aria-label={`下移${combo?.name ?? id}`}><ChevronDown/></button>
          <button onClick={() => setRoutes(routeIds.filter((_, routeIndex) => routeIndex !== index))} aria-label={`移除${combo?.name ?? id}`}><Trash2/></button>
        </div>
      </div>; })}
    </div>}
    <span className={primary ? "route-summary ready" : "route-summary missing"}>{summary}</span>
  </div>;
}

function FeatureRouteRow({ feature, required, expanded, onToggle }: { feature: FeatureId; required: boolean; expanded: boolean; onToggle: () => void }) {
  const settings = useSettingsStore();
  const expectedKind: EndpointCombo["kind"] = feature === "rag-embedding" ? "embedding" : "generation";
  const routeIds = settings.featureRoutes[feature] ?? [];
  const assigned = routeIds.flatMap((id) => { const combo = settings.endpointCombos.find((item) => item.id === id); return combo ? [combo] : []; });
  const usable = assigned.filter((combo) => isUsableCombo(combo, expectedKind));
  const primary = usable[0];
  const summary = primary ? `主：${primary.name}${usable.length > 1 ? ` · 备用 ${usable.length - 1}` : ""}` : routeIds.length ? "已分配，但没有可用接口" : "未分配";
  return <Collapsible.Root className={required && !primary ? "feature-route-row required-missing" : "feature-route-row"} open={expanded} onOpenChange={onToggle}>
    <Collapsible.Trigger className="feature-route-trigger" aria-label={`${expanded ? "收起" : "展开"}${FEATURE_LABELS[feature]}路由配置`}>
      <div><span><strong>{FEATURE_LABELS[feature]}</strong>{required && <em className="required-badge">必配</em>}{RECOMMENDED_MODELS[feature] && <em className="model-badge">{RECOMMENDED_MODELS[feature]}</em>}</span><small className={primary ? "ready" : "missing"}>{summary}</small></div>
      {expanded ? <ChevronUp/> : <ChevronDown/>}
    </Collapsible.Trigger>
    <Collapsible.Content className="feature-route-content"><RouteSelector feature={feature} required={required}/></Collapsible.Content>
  </Collapsible.Root>;
}

export function RouteManager({ onboarding = false }: { onboarding?: boolean }) {
  const settings = useSettingsStore();
  const generation = settings.endpointCombos.filter((combo) => isUsableCombo(combo));
  const [selected, setSelected] = useState(generation[0]?.id ?? "");
  const [expandedFeature, setExpandedFeature] = useState<FeatureId | null>(null);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => Object.fromEntries(GROUPS.map((group) => [group.id, !group.defaultCollapsed])));
  const activeFeatures = useMemo(() => FEATURE_IDS.filter((feature) => settings.evolutionRoutesSplit ? feature !== "evolution" : !EVOLUTION_ROUTE_IDS.includes(feature as typeof EVOLUTION_ROUTE_IDS[number]) && feature !== "evolution-bio"), [settings.evolutionRoutesSplit]);
  const issueCount = useMemo(() => activeFeatures.filter((feature) => !routeIsUsable(feature, settings.endpointCombos, settings.featureRoutes)).length, [activeFeatures, settings.endpointCombos, settings.featureRoutes]);
  const requiredFeatures: FeatureId[] = ["core-chat"];
  if (settings.evolutionEnabled !== false) requiredFeatures.push(...(settings.evolutionRoutesSplit ? EVOLUTION_ROUTE_IDS : ["evolution"] as const));
  const required = new Set<FeatureId>(requiredFeatures);

  useEffect(() => {
    if (!generation.some((combo) => combo.id === selected)) setSelected(generation[0]?.id ?? "");
  }, [generation, selected]);

  const batch = (mode: "overwrite" | "append") => {
    if (!selected) return;
    settings.setManyFeatureRoutes(activeFeatures.filter((feature) => feature !== "rag-embedding").map((feature) => {
      const current = settings.featureRoutes[feature] ?? [];
      return { feature, comboIds: mode === "overwrite" ? [selected] : [...current, selected] };
    }));
  };

  const toggleEvolutionRoutes = () => {
    const split = !settings.evolutionRoutesSplit;
    if (!split) {
      settings.set({ evolutionRoutesSplit: false });
      return;
    }
    const unified = settings.featureRoutes.evolution ?? [];
    const fallback = unified.length ? unified : generation.length === 1 ? [generation[0]!.id] : [];
    const featureRoutes = { ...settings.featureRoutes };
    if (fallback.length) for (const feature of [...EVOLUTION_ROUTE_IDS, "evolution-bio"] as FeatureId[])
      if (!(featureRoutes[feature]?.length)) featureRoutes[feature] = [...fallback];
    settings.set({ evolutionRoutesSplit: true, featureRoutes });
  };

  return <section className={onboarding ? "settings-workspace route-manager onboarding-route-manager" : "settings-workspace route-manager"}>
    <header className="workspace-head"><div><h2>功能路由分配</h2><p>每个功能可以指定主接口和备用接口。调用失败时，会按顺序尝试备用路由。</p>{generation.length === 1 && <small className="single-route-warning"><CircleAlert/>现在只有 1 个可用接口。返回 API 配置再添加一个接口，才能配置真正的备用路由。</small>}</div><span className={issueCount ? "issue-badge" : "ok-badge"}>{issueCount ? `${issueCount} 项待配置` : "路由正常"}</span></header>
    {generation.length ? <div className="route-batch">
      <Select.Root value={selected} onValueChange={setSelected}>
        <Select.Trigger className="route-batch-select" aria-label="选择批量分配接口"><Select.Value placeholder="选择接口"/><Select.Icon><ChevronDown/></Select.Icon></Select.Trigger>
        <Select.Portal><Select.Content className="route-select-content" position="popper" sideOffset={6}><Select.Viewport>{generation.map((combo) => <Select.Item className="route-select-item" key={combo.id} value={combo.id}><Select.ItemText>{combo.name}</Select.ItemText></Select.Item>)}</Select.Viewport></Select.Content></Select.Portal>
      </Select.Root>
      <button disabled={!selected} onClick={() => batch("overwrite")}><Route/>全部使用此接口</button>
      <button disabled={!selected} onClick={() => batch("append")}><Plus/>设为全部功能的备用</button>
    </div> : <div className="route-no-endpoint">请先返回 API 配置添加已启用、配置完整且当前会话已填写密钥的生成接口。</div>}
    <div className="route-groups">{GROUPS.map((group) => { const open = openGroups[group.id] ?? true; return <Collapsible.Root key={group.id} open={open} onOpenChange={(next) => setOpenGroups((current) => ({ ...current, [group.id]: next }))}>
      <section className="route-group"><Collapsible.Trigger className="route-group-trigger"><div><h3>{group.title}</h3><p>{group.description}</p></div>{open ? <ChevronUp/> : <ChevronDown/>}</Collapsible.Trigger>
        <Collapsible.Content className="route-group-content">{group.id === "evolution" ? <>
          <div className="evolution-route-mode"><div><strong>分别配置并发演化阶段路由</strong><p>关闭时，登场判断、物品、猎人、NPC、怪物、杂项和人物传记压缩共用统一路由；开启后才显示各阶段路由。</p></div><button type="button" role="switch" aria-checked={settings.evolutionRoutesSplit} aria-label="分别配置并发演化阶段路由" className={settings.evolutionRoutesSplit ? "route-mode-switch checked" : "route-mode-switch"} onClick={toggleEvolutionRoutes}><span/><em>{settings.evolutionRoutesSplit ? "已分流" : "统一路由"}</em></button></div>
          {settings.evolutionRoutesSplit ? group.features.map((feature) => <FeatureRouteRow key={feature} feature={feature} required={required.has(feature)} expanded={expandedFeature === feature} onToggle={() => setExpandedFeature((current) => current === feature ? null : feature)}/>) : <FeatureRouteRow feature="evolution" required={required.has("evolution")} expanded={expandedFeature === "evolution"} onToggle={() => setExpandedFeature((current) => current === "evolution" ? null : "evolution")}/>}
        </> : group.features.map((feature) => <FeatureRouteRow key={feature} feature={feature} required={required.has(feature)} expanded={expandedFeature === feature} onToggle={() => setExpandedFeature((current) => current === feature ? null : feature)}/>)}</Collapsible.Content>
      </section>
    </Collapsible.Root>; })}</div>
  </section>;
}
